import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useAppContext } from "@/providers";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SiteHeader } from "@/components/layout/site-header";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { t } from "@/lib/i18n/messages";
import { resendOtp } from "@/lib/api/auth";
import { useDocumentTitle } from "@/hooks/use-document-title";

export default function VerifyOtpPage() {
  const { locale } = useAppContext();
  const isRTL = locale === "ar";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";

  useDocumentTitle(`${t(locale, "verifyOtpTitle")} | NCircle`);

  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const onVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp) { setError(t(locale, "requiredField")); return; }
    if (otp.length < 4) { setError(t(locale, "requiredField")); return; }
    setError("");
    void navigate(`/reset-password?email=${encodeURIComponent(email)}&otp=${encodeURIComponent(otp)}`);
  };

  const onResend = async () => {
    if (resendCooldown > 0 || !email) return;
    setResending(true);
    try {
      await resendOtp({ email });
      setResendCooldown(60);
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <SiteHeader />
      <main
        dir={isRTL ? "rtl" : "ltr"}
        className="flex items-center justify-center py-12 px-4 min-h-[calc(100vh-64px)] pt-24 bg-background"
      >
        <Card className="w-full max-w-[480px] p-8 md:p-12 relative overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary via-secondary to-brand-green" />
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">
              {t(locale, "verifyOtpTitle")}
            </h1>
            <p className="text-sm text-foreground-secondary">
              {t(locale, "verifyOtpDesc")}
            </p>
            {email && (
              <p className="text-sm font-medium text-foreground mt-1">
                {email}
              </p>
            )}
          </div>
          <form onSubmit={onVerify} className="space-y-6">
            <Input
              id="otp"
              type="text"
              dir="ltr"
              label={`${t(locale, "otpLabel")} *`}
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              error={error}
              placeholder={t(locale, "otpPlaceholder")}
              maxLength={6}
            />
            <Button type="submit" className="w-full h-12 text-base">
              {t(locale, "verifyOtp")}
            </Button>
          </form>
          <div className="mt-6 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={onResend}
              disabled={resendCooldown > 0 || resending}
              className="text-sm font-medium text-secondary hover:underline disabled:opacity-50 disabled:no-underline"
            >
              {resendCooldown > 0
                ? t(locale, "resendOtpCooldown").replace("{seconds}", String(resendCooldown))
                : t(locale, "resendOtp")}
            </button>
            <Link to="/login" className="text-sm text-foreground-secondary hover:underline">
              {t(locale, "backToLogin")}
            </Link>
          </div>
        </Card>
      </main>
      <ThemeToggle />
    </>
  );
}
